import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { requestPasswordReset } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function BrandHeader() {
  return (
    <div className="mb-6 text-center">
      <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
        <span aria-hidden="true">✚</span> Addy HIS
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Hệ thống quản lý phòng khám</p>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("Vui lòng nhập email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Email không hợp lệ.");
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch {
      // Non-disclosure: even on a transport error we show the same neutral
      // "check your inbox" screen so the form can't be used to probe accounts.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
        <div className="w-full max-w-md">
          <BrandHeader />

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Kiểm tra hộp thư của bạn</CardTitle>
              <CardDescription>
                Nếu <strong className="text-foreground">{email}</strong> tồn tại trong hệ thống,
                chúng tôi đã gửi link đặt lại mật khẩu. Liên kết có hiệu lực trong 60 phút.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
                Mẹo: nếu không thấy email, kiểm tra hộp thư rác hoặc bộ lọc Promotions.
              </div>

              <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
                <Lock className="mt-0.5 shrink-0 text-primary" />
                <div>
                  Vì lý do bảo mật, hệ thống không xác nhận một email có tồn tại trong tài khoản
                  Addy HIS hay không.
                </div>
              </div>

              <Button asChild className="w-full">
                <Link to="/login">Quay lại đăng nhập</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <BrandHeader />

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Quên mật khẩu</CardTitle>
            <CardDescription>
              Nhập email tài khoản — chúng tôi sẽ gửi link đặt lại mật khẩu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoFocus
                  placeholder="bacsi@phongkhamcuaban.vn"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={Boolean(error)}
                  disabled={submitting}
                />
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Đang gửi…" : "Gửi link đặt lại mật khẩu"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Nhớ ra mật khẩu?{" "}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Quay lại đăng nhập
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
